import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocation } from 'wouter';
import api from '../lib/api';
import { ArrowLeft, Search, Store, ChevronRight, AlertCircle } from 'lucide-react';

interface StoreItem {
  id: string;
  name: string;
  cnpj?: string;
  address?: string;
  city?: string;
  state?: string;
}

/**
 * Busca livre de loja para a Pesquisa PDV avulsa (agências sem roteiro).
 * Home → Pesquisa PDV → escolher loja → pesquisas da loja.
 */
export default function StorePicker() {
  const [, setLocation] = useLocation();
  const [term, setTerm] = useState('');
  const [debounced, setDebounced] = useState('');

  // debounce simples
  useDebounce(term, 400, setDebounced);

  const { data, isLoading, isError, refetch, isFetching } = useQuery<StoreItem[]>({
    queryKey: ['stores', 'picker', debounced],
    queryFn: () =>
      api
        .get(`/stores?limit=20${debounced.trim() ? `&search=${encodeURIComponent(debounced.trim())}` : ''}`)
        .then((res) => res.data.data),
  });

  const stores = data ?? [];

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      <header className="bg-primary text-white p-4">
        <div className="flex items-center gap-4 mb-4">
          <button
            onClick={() => setLocation('/')}
            className="p-2 -ml-2 rounded-full hover:bg-white/20"
            aria-label="Voltar para o início"
          >
            <ArrowLeft className="h-6 w-6" />
          </button>
          <div className="flex-1">
            <h1 className="text-lg font-bold">Pesquisa PDV</h1>
            <p className="text-sm text-white/80">Escolha a loja para pesquisar</p>
          </div>
        </div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            placeholder="Buscar por nome, CNPJ ou cidade"
            className="w-full pl-10 pr-4 py-3 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none"
            aria-label="Buscar loja"
          />
        </div>
      </header>

      <div className="flex-1 p-4 space-y-3">
        {(isLoading || isFetching) && stores.length === 0 && (
          <>
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-16 rounded-xl bg-gray-200 animate-pulse" aria-hidden />
            ))}
          </>
        )}

        {isError && (
          <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
            <AlertCircle className="h-10 w-10 text-red-500" />
            <p className="text-gray-700">Não foi possível carregar as lojas.</p>
            <button
              onClick={() => refetch()}
              className="px-4 py-2 rounded-lg bg-primary text-white text-sm font-medium"
            >
              Tentar novamente
            </button>
          </div>
        )}

        {!isLoading && !isError && stores.length === 0 && (
          <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
            <Store className="h-10 w-10 text-gray-400" />
            <p className="text-gray-700 font-medium">Nenhuma loja encontrada</p>
            <p className="text-sm text-gray-500">Tente outro termo de busca.</p>
          </div>
        )}

        {!isError &&
          stores.map((store) => (
            <button
              key={store.id}
              onClick={() => setLocation(`/pesquisa-pdv/${store.id}`)}
              className="w-full flex items-center gap-4 p-4 bg-white rounded-xl shadow-sm border border-gray-100 active:bg-gray-50 text-left"
            >
              <div className="p-3 rounded-full bg-sky-100 flex-shrink-0">
                <Store className="h-6 w-6 text-sky-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-900 truncate">{store.name}</p>
                <p className="text-sm text-gray-500 truncate">
                  {[store.city, store.state].filter(Boolean).join(' - ') || store.address || store.cnpj}
                </p>
              </div>
              <ChevronRight className="h-5 w-5 text-gray-400 flex-shrink-0" />
            </button>
          ))}
      </div>
    </div>
  );
}

// debounce hook local (evita dep externa)
import { useEffect } from 'react';
function useDebounce(value: string, delay: number, onChange: (v: string) => void) {
  useEffect(() => {
    const t = setTimeout(() => onChange(value), delay);
    return () => clearTimeout(t);
  }, [value, delay, onChange]);
}
